import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import styled from 'styled-components';

import { updateShopRequest, validateShopRequest } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import { isMysteryShopShopper } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import {
  SHOP_REQUEST_PROPAGATION_MODAL_MODE,
  SHOP_REQUEST_STATUS,
  SHOP_REQUEST_STATUS_LABELS,
  SHOP_REQUEST_TYPE,
} from '../constants';
import EditNoteButton from '../NoteButton/EditNoteButton';
import NotePopover from '../NoteButton/NotePopover';
import NoteModal from '../NoteModal/NoteModal';
import ShopRequestPropagateModal from '../propagate/ShopRequestPropagateModal';
import { useShopRequestsDataContext } from '../ShopRequestsDataContext';
import ShopRequestStatusSelector from '../ShopRequestStatusSelector';
import { MysteryShopRequestInbound, MysteryShopRequestOutbound } from '../types';
import { getShopRequestPropagationCommunities } from '../utils';
import ShopRequestValidationModal, { ShopRequestValidationWarning } from '../Validation/ShopRequestValidationModal';

const Container = styled.div<{ editNoteButtonOpacity: number }>`
  display: flex;
  flex-flow: row;
  align-items: center;
  .note-button-container {
    margin-left: 10px;
    opacity: ${(props) => props.editNoteButtonOpacity};
  }
`;

const CommunityName = styled.span`
  color: var(--dark-blue);
`;

type Props = {
  shopRequest: MysteryShopRequestInbound;
};

const ShopRequestsStatus = ({ shopRequest }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const { filters, ordering } = useShopRequestsDataContext();

  const [isLoading, setLoading] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<number | null>(null);
  const [newNote, setNewNote] = useState<string | undefined>(undefined);
  const [showPropagationModal, setShowPropagationModal] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState<ShopRequestValidationWarning[]>([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [pendingValidationAction, setPendingValidationAction] = useState<(() => void) | null>(null);

  const { refetch: refetchValidation, isFetching: isValidationLoading } = useQuery({
    queryKey: ['shopRequestValidation', shopRequest.id],
    queryFn: () => validateShopRequest(shopRequest.id),
    enabled: false,
    refetchOnWindowFocus: false,
  });

  const isShopRequestSalesperson = isMysteryShopShopper(currentUser);
  const isPricingRequest = shopRequest.request_type === SHOP_REQUEST_TYPE.MYSTERY_SHOP;
  const baseCommunityId = shopRequest.requestGroup?.base_community?.id ?? shopRequest.requestGroup?.base_community_id;

  useEffect(() => {
    if (shopRequest?.status) {
      setNewStatus(shopRequest?.status);
    }
  }, [shopRequest]);
  const openUpdateStatusModal = async (status: number) => {
    setNewStatus(status);
    setStatusModalOpen(true);
  };

  const handleValidationClose = () => {
    setShowValidationModal(false);
    setValidationWarnings([]);
    setPendingValidationAction(null);
    setNewNote(undefined);
    setNewStatus(null);
  };

  const handleValidationConfirm = () => {
    setShowValidationModal(false);
    const action = pendingValidationAction;
    setPendingValidationAction(null);
    setValidationWarnings([]);
    if (action) {
      action();
    }
  };

  const runValidation = async (action: () => void) => {
    setPendingValidationAction(() => action);
    try {
      const result = await refetchValidation();
      const warnings = Array.isArray(result.data) ? result.data : [];
      if (warnings.length > 0) {
        setValidationWarnings(warnings);
        setShowValidationModal(true);
        return;
      }
      setPendingValidationAction(null);
      action();
    } catch (error) {
      setPendingValidationAction(null);
      showMessage('error', 'Failed to validate shop request');
    }
  };

  const handleUpdate = (note: string) => {
    const proceed = () => {
      if (isPricingRequest && newStatus === SHOP_REQUEST_STATUS_LABELS.APPROVED) {
        setStatusModalOpen(true);
        setShowPropagationModal(true);
        setNewNote(note);
      } else {
        updateShopRequestData({
          shopper_note: note,
          status: newStatus as number,
        });
      }
    };

    if (newStatus === SHOP_REQUEST_STATUS_LABELS.NEEDS_APPROVAL || newStatus === SHOP_REQUEST_STATUS_LABELS.APPROVED) {
      runValidation(proceed);
      return;
    }

    proceed();
  };

  const handleApproveUpdate = () => {
    const payload: Partial<MysteryShopRequestOutbound> = {
      status: newStatus as number,
      shopper_note: newNote,
      propagation: shopRequest.propagation,
    };
    updateShopRequestData(payload).then(() => {
      onClosePropagationModal();
    });
  };

  const updateShopRequestData = async (payload: Partial<MysteryShopRequestOutbound>) => {
    setLoading(true);
    await updateShopRequest(shopRequest.id, payload)
      .then(() => {
        queryClient.invalidateQueries(['shopRequests', filters, ordering]);
        queryClient.invalidateQueries(['shopRequestsGroups', filters, ordering]);
        showMessage('success', 'Shop request updated successfully');
      })
      .catch((error) => {
        showMessage('error', 'Failed to update shop request');
      })
      .finally(() => {
        setLoading(false);
        setEditNoteModalOpen(false);
      });
  };

  const onCloseModal = () => {
    setEditNoteModalOpen(false);
    setStatusModalOpen(false);

    // This status needs additional approval, it will be reset when additional modal is closed
    if (newStatus !== SHOP_REQUEST_STATUS_LABELS.APPROVED && !pendingValidationAction) {
      setNewStatus(null);
    }
  };

  const onClosePropagationModal = () => {
    setShowPropagationModal(false);
    setNewStatus(null);
  };

  if (!shopRequest.status) {
    return <div />;
  }

  return (
    <Container
      editNoteButtonOpacity={shopRequest.shopperNote ? 1 : 0}
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      className="status-column"
    >
      <ShopRequestStatusSelector
        shopRequest={shopRequest}
        disabled={!!isShopRequestSalesperson && currentUser?.PAUser_Id !== shopRequest?.shopper?.userId}
        onChange={openUpdateStatusModal}
        dataTestId="dropdown-status"
      />
      {shopRequest.shopperNote || shopRequest.adminNote ? (
        <NotePopover shopRequest={shopRequest} />
      ) : (
        <EditNoteButton
          isModalOpen={editNoteModalOpen}
          note={shopRequest.shopperNote}
          okButtonDisabled={isLoading}
          setModalOpen={setEditNoteModalOpen}
          onCloseModal={onCloseModal}
          modifyNote={handleUpdate}
        />
      )}
      {statusModalOpen && (
        <NoteModal
          title={`Change to ${SHOP_REQUEST_STATUS.find((item) => item.id === newStatus)?.label}?`}
          note={shopRequest.shopperNote}
          isModalOpen={statusModalOpen}
          closeModal={onCloseModal}
          modifyNote={handleUpdate}
          disableEmptyNote={newStatus === SHOP_REQUEST_STATUS_LABELS.BLOCKED}
        />
      )}
      {showPropagationModal && (
        <ShopRequestPropagateModal
          mode={SHOP_REQUEST_PROPAGATION_MODAL_MODE.APPROVE}
          subtitle={<CommunityName>{shopRequest?.community?.name}</CommunityName>}
          baseCommunityId={baseCommunityId}
          communities={getShopRequestPropagationCommunities(shopRequest)}
          open={showPropagationModal}
          onConfirm={handleApproveUpdate}
          onCancel={onClosePropagationModal}
        />
      )}
      {showValidationModal && (
        <ShopRequestValidationModal
          open={showValidationModal}
          warnings={validationWarnings}
          onConfirm={handleValidationConfirm}
          onClose={handleValidationClose}
          confirmLoading={isValidationLoading}
        />
      )}
    </Container>
  );
};

export default ShopRequestsStatus;
